import {
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Box,
  Divider,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

export interface CommunicationResumeData {
  id: string;
  title: string;
  scheduledDate: string;
  status: 'Brouillon' | 'Planifiée' | 'Envoyée';
  nbRecipients: string;
  sendByCustomer: boolean;
  templateAssociated: boolean;
  offers: Array<{ name: string; scheduledDate: string }>;
}

interface CommunicationResumeCardProps {
  data: CommunicationResumeData;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'info'> = {
  Brouillon: 'default',
  Planifiée: 'info',
  Envoyée: 'success',
};

function CommunicationResumeCard({ data, onEdit, onDelete }: CommunicationResumeCardProps) {
  const { sidebarColor } = useLayoutPreferences();

  const linkBtnSx = {
    color: sidebarColor,
    textTransform: 'none',
    fontSize: '0.75rem',
    p: 0,
    minWidth: 'unset',
    '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        mb: 2,
        '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
        transition: 'box-shadow 0.2s',
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, sm: 3 }}>
            <Typography variant="body2" fontWeight={700}>{data.title}</Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {data.scheduledDate === 'Envoi non programmé'
                ? 'Envoi non programmé'
                : `Envoi programmé au ${data.scheduledDate}`}
            </Typography>
            <Chip
              label={data.status}
              size="small"
              color={STATUS_COLORS[data.status] ?? 'default'}
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          </Grid>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
              Offre(s)
            </Typography>
            <Grid container spacing={1}>
              {data.offers.map((offer, idx) => (
                <Grid size={6} key={idx}>
                  <Typography variant="caption" fontWeight={600} display="block">{offer.name}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {offer.scheduledDate}
                  </Typography>
                  <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: '0.75rem !important' }} />} sx={linkBtnSx}>
                    Visualiser
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <Grid size={{ xs: 12, sm: 4 }}>
            <Grid container spacing={1}>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Nb. de destinataires
                </Typography>
                <Typography variant="body2" fontWeight={600}>{data.nbRecipients}</Typography>
              </Grid>
              <Grid size={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Envoi par le client
                </Typography>
                <Typography variant="body2" fontWeight={600}>{data.sendByCustomer ? 'OUI' : 'NON'}</Typography>
              </Grid>
              <Grid size={12}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                  Template associé
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={600}>{data.templateAssociated ? 'OUI' : 'NON'}</Typography>
                  {data.templateAssociated && (
                    <Button size="small" startIcon={<VisibilityIcon sx={{ fontSize: '0.75rem !important' }} />} sx={linkBtnSx}>
                      Visualiser
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>

          <Grid size="auto" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', ml: 'auto' }}>
            <IconButton size="small" sx={{ color: sidebarColor }} onClick={() => onEdit?.(data.id)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" sx={{ color: '#dc3545' }} onClick={() => onDelete?.(data.id)}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

export default CommunicationResumeCard;
